"use client";

import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";

import { cn } from "@acme/ui/";

import { buttonVariants } from "./utils/button-variants";

export interface IconProps {
  icon: React.ElementType;
  iconPlacement: "left" | "right";
}

export interface IconRefProps {
  icon?: never;
  iconPlacement?: undefined;
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export type ButtonIconProps = IconProps | IconRefProps;

// Spinner width constant for maintainability
const SPINNER_WIDTH = "1.25rem";

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps & ButtonIconProps
>(
  (
    {
      className,
      variant,
      effect,
      size,
      icon: Icon,
      iconPlacement,
      asChild = false,
      loading = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";

    // Spinner motion props for reuse and clarity
    const spinnerMotionProps = {
      initial: { width: 0, opacity: 0 },
      animate: { width: SPINNER_WIDTH, opacity: 1 },
      exit: { width: 0, opacity: 0 },
      transition: { duration: 0.15 },
    };

    // Early return: loading spinner only for icon size
    if (loading && size === "icon") {
      return (
        <Comp
          className={cn(buttonVariants({ variant, effect, size, className }))}
          ref={ref}
          disabled
          {...props}
        >
          <Loader2 className="animate-spin" />
          <span className="sr-only">Loading</span>
        </Comp>
      );
    }

    // Normal button rendering
    return (
      <Comp
        className={cn(buttonVariants({ variant, effect, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      >
        <AnimatePresence initial={false} mode="wait">
          {loading && (
            <motion.span
              key="spinner"
              {...spinnerMotionProps}
              className={"inline-flex items-center overflow-hidden"}
              style={{ width: undefined }}
            >
              <Loader2 className="animate-spin" aria-hidden="true" />
              <span className="sr-only">Loading</span>
            </motion.span>
          )}
        </AnimatePresence>
        {/* Left icon (if not loading) */}
        {!loading &&
          Icon &&
          iconPlacement === "left" &&
          (effect === "expandIcon" ? (
            <div className="w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-100 group-hover:pr-2 group-hover:opacity-100">
              <Icon />
            </div>
          ) : (
            <Icon />
          ))}
        {/* Children */}
        <Slottable>{props.children}</Slottable>
        {/* Right icon (if any, and not loading) */}
        {Icon &&
          iconPlacement === "right" &&
          !loading &&
          (effect === "expandIcon" ? (
            <div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
              <Icon />
            </div>
          ) : (
            <Icon />
          ))}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
